/**
 * Signal Store - IndexedDB + localStorage persistent storage for Signal Protocol state
 */
import { encodeBase64, decodeBase64 } from 'tweetnacl-util'
import type {
  KeyPair, SignedPreKey, OneTimePreKey, SessionState,
} from './signal-protocol'
import { serializeSession, deserializeSession } from './signal-protocol'

const STK = {
  IK: 'lynkstar_identity_key',
  SPK: 'lynkstar_signed_pre_key',
  SESS: 'lynkstar_sessions_v5',
  UUID: 'lynkstar_my_uuid',
  OPKN: 'lynkstar_opk_next_id',
}

export class SignalStore {
  private db: IDBDatabase | null = null
  private ok = true

  async init(): Promise<void> {
    if (typeof indexedDB === 'undefined') { this.ok = false; return }
    try {
      this.db = await new Promise<IDBDatabase>((res, rej) => {
        const r = indexedDB.open('lynkstar_signal', 1)
        r.onupgradeneeded = () => {
          const d = r.result
          for (const n of ['sessions', 'identities', 'prekeys']) {
            if (!d.objectStoreNames.contains(n)) d.createObjectStore(n)
          }
        }
        r.onsuccess = () => res(r.result)
        r.onerror = () => rej(r.error)
      })
    } catch { this.ok = false }
  }

  // ---- Internal IDB helpers ----

  private async _get(s: string, k: string): Promise<string | null> {
    if (!this.db) return null
    return new Promise(r => {
      try {
        const t = this.db!.transaction(s, 'readonly')
        const q = t.objectStore(s).get(k)
        q.onsuccess = () => r(q.result ?? null)
        q.onerror = () => r(null)
      } catch { r(null) }
    })
  }

  private async _put(s: string, k: string, v: string): Promise<void> {
    if (!this.db) return
    return new Promise(r => {
      try {
        const t = this.db!.transaction(s, 'readwrite')
        t.objectStore(s).put(v, k)
        t.oncomplete = () => r()
        t.onerror = () => r()
      } catch { r() }
    })
  }

  private async _del(s: string, k: string): Promise<void> {
    if (!this.db) return
    return new Promise(r => {
      try {
        const t = this.db!.transaction(s, 'readwrite')
        t.objectStore(s).delete(k)
        t.oncomplete = () => r()
        t.onerror = () => r()
      } catch { r() }
    })
  }

  // ---- Identity Key Pair ----

  async saveIdentityKeyPair(kp: KeyPair): Promise<void> {
    const d = JSON.stringify({
      publicKey: encodeBase64(kp.publicKey),
      secretKey: encodeBase64(kp.secretKey),
    })
    localStorage.setItem(STK.IK, d)
    if (this.ok) await this._put('identities', 'self', d)
  }

  async loadIdentityKeyPair(): Promise<KeyPair | null> {
    try {
      let d = this.ok ? await this._get('identities', 'self') : null
      if (!d) d = localStorage.getItem(STK.IK)
      if (!d) return null
      const p = JSON.parse(d)
      return { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) }
    } catch { return null }
  }

  // ---- Remote Identity ----

  async saveRemoteIdentity(userId: string, publicKey: Uint8Array): Promise<void> {
    const d = encodeBase64(publicKey)
    localStorage.setItem(`lynkstar_remote_ik_${userId}`, d)
    if (this.ok) await this._put('identities', `remote:${userId}`, d)
  }

  async loadRemoteIdentity(userId: string): Promise<Uint8Array | null> {
    try {
      let d = this.ok ? await this._get('identities', `remote:${userId}`) : null
      if (!d) d = localStorage.getItem(`lynkstar_remote_ik_${userId}`)
      if (!d) return null
      return decodeBase64(d)
    } catch { return null }
  }

  // ---- Sessions ----

  async saveSession(uid: string, sess: SessionState): Promise<void> {
    const s = serializeSession(sess)
    const all = JSON.parse(localStorage.getItem(STK.SESS) || '{}')
    all[uid] = s
    localStorage.setItem(STK.SESS, JSON.stringify(all))
    if (this.ok) await this._put('sessions', uid, JSON.stringify(s))
  }

  async loadSession(uid: string): Promise<SessionState | null> {
    try {
      let d = this.ok ? await this._get('sessions', uid) : null
      if (d) return deserializeSession(JSON.parse(d))
      const all = JSON.parse(localStorage.getItem(STK.SESS) || '{}')
      if (!all[uid]) return null
      return deserializeSession(all[uid])
    } catch { return null }
  }

  async deleteSession(uid: string): Promise<void> {
    try {
      const all = JSON.parse(localStorage.getItem(STK.SESS) || '{}')
      delete all[uid]
      localStorage.setItem(STK.SESS, JSON.stringify(all))
      if (this.ok) await this._del('sessions', uid)
    } catch { /* ignore */ }
  }

  // ---- Signed Pre-Key ----

  async saveSignedPreKey(spk: SignedPreKey): Promise<void> {
    const d = JSON.stringify({
      keyId: spk.keyId,
      publicKey: encodeBase64(spk.keyPair.publicKey),
      secretKey: encodeBase64(spk.keyPair.secretKey),
      signature: encodeBase64(spk.signature),
    })
    localStorage.setItem(`${STK.SPK}_${spk.keyId}`, d)
    if (this.ok) await this._put('prekeys', `spk:${spk.keyId}`, d)
  }

  async loadSignedPreKey(kid: number): Promise<SignedPreKey | null> {
    try {
      let d = this.ok ? await this._get('prekeys', `spk:${kid}`) : null
      if (!d) d = localStorage.getItem(`${STK.SPK}_${kid}`)
      if (!d) return null
      const p = JSON.parse(d)
      return {
        keyId: p.keyId,
        keyPair: { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) },
        signature: decodeBase64(p.signature),
      }
    } catch { return null }
  }

  // ---- One-Time Pre-Keys ----

  async saveOneTimePreKey(opk: OneTimePreKey): Promise<void> {
    const d = JSON.stringify({
      keyId: opk.keyId,
      publicKey: encodeBase64(opk.keyPair.publicKey),
      secretKey: encodeBase64(opk.keyPair.secretKey),
    })
    localStorage.setItem(`lynkstar_otpk_${opk.keyId}`, d)
    if (this.ok) await this._put('prekeys', `otpk:${opk.keyId}`, d)
  }

  async loadOneTimePreKey(kid: number): Promise<OneTimePreKey | null> {
    try {
      let d = this.ok ? await this._get('prekeys', `otpk:${kid}`) : null
      if (!d) d = localStorage.getItem(`lynkstar_otpk_${kid}`)
      if (!d) return null
      const p = JSON.parse(d)
      return {
        keyId: p.keyId,
        keyPair: { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) },
      }
    } catch { return null }
  }

  async deleteOneTimePreKey(kid: number): Promise<void> {
    localStorage.removeItem(`lynkstar_otpk_${kid}`)
    if (this.ok) await this._del('prekeys', `otpk:${kid}`)
  }

  // ---- Next OPK ID tracker ----

  getNextOPKId(): number {
    const v = localStorage.getItem(STK.OPKN)
    return v ? parseInt(v, 10) : 1
  }

  setNextOPKId(id: number): void {
    localStorage.setItem(STK.OPKN, String(id))
  }
}

// Singleton
let _store: SignalStore | null = null
export async function getSignalStore(): Promise<SignalStore> {
  if (!_store) {
    _store = new SignalStore()
    await _store.init()
  }
  return _store
}
